import React, { useState, useEffect } from 'react';
import { useProfileStore } from '@/stores/useProfileStore';
import { useMedicationStore } from '@/store/medicationStore';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle, Info, LifeBuoy, Activity } from 'lucide-react';
import { OptimizedHeader } from '@/components/layout/OptimizedHeader';
import { toast } from 'sonner';

interface LogEntry {
  level: 'log' | 'error' | 'warn' | 'info';
  timestamp: Date;
  content: any[];
}

const Debugger: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const originalConsoleLog = console.log;
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;
  const originalConsoleInfo = console.info;

  useEffect(() => {
    console.log = (...args) => {
      setLogs(prev => [...prev, {
        level: 'log',
        timestamp: new Date(),
        content: args
      }]);
      originalConsoleLog(...args);
    };

    console.error = (...args) => {
      setLogs(prev => [...prev, {
        level: 'error',
        timestamp: new Date(),
        content: args
      }]);
      originalConsoleError(...args);
    };

    console.warn = (...args) => {
      setLogs(prev => [...prev, {
        level: 'warn',
        timestamp: new Date(),
        content: args
      }]);
      originalConsoleWarn(...args);
    };

    console.info = (...args) => {
      setLogs(prev => [...prev, {
        level: 'info',
        timestamp: new Date(),
        content: args
      }]);
      originalConsoleInfo(...args);
    };

    return () => {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      console.info = originalConsoleInfo;
    };
  }, []);

  return (
    <div className="container mx-auto p-4">
      <OptimizedHeader />
      <div className="space-y-4">
        {logs.map((log, index) => (
          <div key={index} className={`p-2 rounded ${
            log.level === 'error' ? 'bg-red-100' :
            log.level === 'warn' ? 'bg-yellow-100' :
            log.level === 'info' ? 'bg-blue-100' :
            'bg-gray-100'
          }`}>
            <div className="text-xs text-gray-500">
              {log.timestamp.toISOString()}
            </div>
            <div className="font-mono">
              {log.content.map((item, i) => (
                <div key={i}>{typeof item === 'object' ? JSON.stringify(item, null, 2) : String(item)}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Debugger;